import preDefinedAnswerSummaryConfigs from "@components/shared/form/answerSummary/preDefinedConfigs";
import { Category } from "@lib/shared/types";
import { extractContentFromHtml } from "@lib/sharedUtils";
import { useMemo } from "react";
import { DomainInfo } from "src/api/domainInfo";
import { DomainForm } from "src/api/domainForm";
import { AnswerSummaryConfigs } from "@components/shared/form";
interface BackButton {
    firstStepAction?: () => void;
    label?: string;
    class?: string;
}

export interface UseFormRenderConditionsParams {
    showProgress: boolean | undefined;
    globalLoading: boolean;
    otp: { isRequired?: boolean; isResolved?: boolean };
    formErrors: number;
    showFormModal: boolean;
    operation: { key?: string } | null;
    stepsConfig: Array<{ hideProgress?: boolean }> | undefined;
    currentStep: number;
    formSubmitted: boolean;
    steps: unknown[];
    domain: DomainInfo;
    preFirstStepTitle: string | undefined;
    formTemplate: string | string[] | null | undefined;
    formTemplateProp: string | null | undefined;
    sellingPointsSteps: string | undefined;
    sellingPoint: string | undefined;
    isLastStep: boolean;
    visitDetails: { region?: string; city?: string } | undefined;
    locale: string | undefined;
    backButton: BackButton | undefined;
    handlePrevStep: () => void;
    isFormInsideOffersModal: boolean;
    goToListPage: boolean | undefined;
    category: Category | null | undefined;
    formOffer: unknown[] | undefined;
    // For submitButtonText
    form: DomainForm | null | undefined;
    // For isSecondServiceSupported / shouldRenderSecondServiceForm
    isMainFormSubmitted: boolean;
    isPiiCompletionForm: boolean | undefined;
    isSecondServiceForm: boolean | undefined;
    // For shouldShowSkipThankYouShortcut
    skipToThankYou: boolean;
    goToThankYouPage: boolean | undefined;
}

export interface UseFormRenderConditionsResult {
    finalAnswerSummaryConfigs: AnswerSummaryConfigs | undefined;
    isRoundTwo: boolean;
    shouldShowProgressBar: boolean | undefined;
    shouldShowBackButton: boolean | undefined;
    progressTotal: number;
    shouldShowPreFirstStepTitle: boolean;
    isV1Template: boolean;
    isV2Template: boolean;
    shouldShowOffersInFormTop: boolean | undefined;
    shouldShowOffersInFormBottom: boolean | undefined;
    shouldShowFieldWrapperProgress: boolean | undefined;
    shouldShowFieldWrapperBackButton: boolean;
    shouldShowSellingPoint: boolean;
    sellingPointLabel: string | undefined;
    shouldShowFloatingTcpa: boolean;
    handleBackClick: () => void;
    shouldShowFormInOffersExitModal: boolean;
    showOtp: boolean;
    showBackButton: boolean | undefined;
    showOfferInForm: boolean | undefined;
    showTCPAMessage: boolean;
    isSecondServiceSupported: boolean;
    shouldShowSkipThankYouShortcut: boolean;
    shouldRenderSecondServiceForm: boolean;
    submitButtonText: string | undefined;
}

export function useFormRenderConditions({
    showProgress,
    globalLoading,
    otp,
    formErrors,
    showFormModal,
    operation,
    stepsConfig,
    currentStep,
    formSubmitted,
    steps,
    domain,
    preFirstStepTitle,
    formTemplate,
    formTemplateProp,
    sellingPointsSteps,
    sellingPoint,
    isLastStep,
    visitDetails,
    locale,
    backButton,
    handlePrevStep,
    isFormInsideOffersModal,
    goToListPage,
    category,
    formOffer,
    form,
    isMainFormSubmitted,
    isPiiCompletionForm,
    isSecondServiceForm,
    skipToThankYou,
    goToThankYouPage,
}: UseFormRenderConditionsParams): UseFormRenderConditionsResult {
    const showOtp =
        otp?.isRequired || (!otp?.isRequired && otp?.isResolved) || false;

    const showTCPAMessage: boolean = useMemo(() => {
        return !!(
            steps &&
            (steps as Array<{ includeTCPAMessage?: boolean }>)[currentStep]
                ?.includeTCPAMessage &&
            !formSubmitted &&
            (!otp?.isRequired || operation?.key === "ROUND_TWO")
        );
    }, [formSubmitted, otp, operation?.key]);

    const showBackButton: boolean | undefined = useMemo(() => {
        return (
            (currentStep !== 0 && !formSubmitted && !otp.isRequired) ||
            !!(backButton?.firstStepAction && currentStep === 0)
        );
    }, [backButton, currentStep, steps, formSubmitted]);

    const showOfferInForm =
        currentStep === 0 &&
        !!formOffer?.length &&
        !!(formTemplate === "v1" || formTemplate === "v2" || formTemplateProp);

    const isSecondServiceSupported = useMemo(() => {
        const selectedCategory = domain.categories.find(
            (categoryElement) => categoryElement.slug === category?.slug,
        );
        return !!(
            selectedCategory &&
            selectedCategory?.secondServiceCategories?.length
        );
    }, [category]);

    const submitButtonText = useMemo(() => {
        const textBlocks = form?.textBlocks;
        const secondStepButtonText = textBlocks?.find(
            (item) =>
                item.position ===
                "form-inside-exit-modal-submit-button-text-second-step",
        );
        return secondStepButtonText?.text && isFormInsideOffersModal
            ? extractContentFromHtml(secondStepButtonText.text).trim()
            : (steps as Array<{ buttonText?: string }>)[currentStep]
                  ?.buttonText;
    }, [form, isFormInsideOffersModal, currentStep]);

    const shouldShowSkipThankYouShortcut =
        (skipToThankYou && !goToThankYouPage) ||
        !!(formSubmitted && isSecondServiceForm && !goToThankYouPage);

    const shouldRenderSecondServiceForm =
        isSecondServiceSupported &&
        isMainFormSubmitted &&
        !isPiiCompletionForm &&
        !isSecondServiceForm &&
        !showFormModal;

    const finalAnswerSummaryConfigs = domain.config
        ?.preDefinedAnswerSummaryConfigs
        ? preDefinedAnswerSummaryConfigs[
              domain.config.preDefinedAnswerSummaryConfigs
          ]
        : undefined;

    const isRoundTwo = operation?.key === "ROUND_TWO";

    const shouldShowProgressBar =
        showProgress &&
        !globalLoading &&
        !otp?.isRequired &&
        !formErrors &&
        !showOfferInForm &&
        !showFormModal &&
        !isRoundTwo &&
        !stepsConfig?.[currentStep]?.hideProgress;

    const shouldShowBackButton =
        showBackButton &&
        !formErrors &&
        !showOtp &&
        !showFormModal &&
        !globalLoading &&
        !isRoundTwo;

    const progressTotal = formSubmitted ? steps?.length : steps?.length + 1;

    const shouldShowPreFirstStepTitle = !!(
        domain?.config?.showPreFirstStepTitle &&
        preFirstStepTitle &&
        currentStep === 0
    );

    const isV1Template = formTemplate === "v1" || formTemplateProp === "v1";
    const isV2Template = formTemplate === "v2" || formTemplateProp === "v2";
    const shouldShowOffersInFormTop = showOfferInForm && isV1Template;
    const shouldShowOffersInFormBottom = showOfferInForm && isV2Template;

    const shouldShowFieldWrapperProgress =
        showProgress && !otp?.isRequired && !formErrors && !showOfferInForm;
    const shouldShowFieldWrapperBackButton =
        !!showBackButton && !formErrors && !showOtp;

    const isSellingPointForFirstStepOnly =
        sellingPointsSteps === "FIRST_STEP" && currentStep === 0;
    const isSellingPointForAllSteps = sellingPointsSteps === "ALL_STEPS";
    const shouldShowSellingPoint =
        !!sellingPoint &&
        !isLastStep &&
        (isSellingPointForAllSteps || isSellingPointForFirstStepOnly);

    const sellingPointLabel =
        !visitDetails?.region && locale === "es"
            ? "Mejores abogados de accidentes de auto cerca de mí"
            : sellingPoint;

    const isTcpaPositionAboveSubmit =
        domain.config?.tcpaPosition === "aboveSubmitButton" ||
        domain.categories?.[0].config?.tcpaPosition === "aboveSubmitButton";
    const shouldShowFloatingTcpa =
        !showFormModal && !globalLoading && !isTcpaPositionAboveSubmit;

    const handleBackClick = () => {
        if (backButton?.firstStepAction && currentStep === 0) {
            backButton.firstStepAction();
        } else {
            handlePrevStep();
        }
    };

    const shouldShowFormInOffersExitModal =
        !isFormInsideOffersModal &&
        !goToListPage &&
        !category?.config?.hideExitModalWithForm;

    return {
        finalAnswerSummaryConfigs,
        isRoundTwo,
        shouldShowProgressBar,
        shouldShowBackButton,
        progressTotal,
        shouldShowPreFirstStepTitle,
        isV1Template,
        isV2Template,
        shouldShowOffersInFormTop,
        shouldShowOffersInFormBottom,
        shouldShowFieldWrapperProgress,
        shouldShowFieldWrapperBackButton,
        shouldShowSellingPoint,
        sellingPointLabel,
        shouldShowFloatingTcpa,
        handleBackClick,
        shouldShowFormInOffersExitModal,
        showOtp,
        showBackButton,
        showOfferInForm,
        showTCPAMessage,
        isSecondServiceSupported,
        shouldShowSkipThankYouShortcut,
        shouldRenderSecondServiceForm,
        submitButtonText,
    };
}
